import type { WineListRenderItem } from '../types/venue';

/**
 * Sort modes shared by the admin Preview panel and the customer-facing
 * /m/preview-mock prototype.
 *
 * "Default order" groups by wine color in the natural reading sequence
 * (sparkling → white → rosé → red → sweet → fortified → other) and falls
 * back to the server-supplied position within each color group. This makes
 * a long mixed list scan like a printed wine list out of the box; admins
 * can always override with one of the explicit sort modes.
 */
export type WineListSortMode =
  | 'DEFAULT'
  | 'PRICE_ASC'
  | 'PRICE_DESC'
  | 'VINTAGE_YOUNG_FIRST'
  | 'VINTAGE_OLD_FIRST'
  | 'COLOR_WHITE_FIRST'
  | 'COLOR_RED_FIRST';

/**
 * Display labels for the sort dropdown. Kept here so admin and prototype UIs
 * stay perfectly aligned; if you rename a label, both surfaces update.
 */
export const WINE_LIST_SORT_LABELS: Record<WineListSortMode, string> = {
  DEFAULT: 'Default order',
  PRICE_ASC: 'Price: Low to High',
  PRICE_DESC: 'Price: High to Low',
  VINTAGE_YOUNG_FIRST: 'Vintage: Young to Old',
  VINTAGE_OLD_FIRST: 'Vintage: Old to Young',
  COLOR_WHITE_FIRST: 'Color: White to Red',
  COLOR_RED_FIRST: 'Color: Red to White',
};

/**
 * Canonical wine-color order used for color sorting. Lower rank sorts first
 * in "White to Red" and last in "Red to White". Anything missing from this
 * map (or a null color) sorts to the end in both directions.
 *
 * The system stores `WHITE`, `RED`, `PINK_ROSE` today; other categories are
 * pre-defined here so future enrichments don't need a code change.
 */
const COLOR_ORDER: Record<string, number> = {
  WHITE: 0,
  ROSE: 1,
  PINK_ROSE: 1,   // alias of ROSE — current data uses this
  RED: 2,
  SPARKLING: 3,
  ORANGE: 4,
  SWEET: 5,
  FORTIFIED: 6,
  OTHER: 7,
};

const colorRank = (color: string | null): number | null => {
  if (!color) return null;
  const rank = COLOR_ORDER[color];
  return rank == null ? COLOR_ORDER.OTHER : rank;
};

/**
 * Color order used by the **default** sort. Different from COLOR_ORDER
 * (which is the white→red explicit-sort axis). This one matches the natural
 * "how a wine list reads" sequence:
 *   sparkling → white → rosé → red → sweet → fortified → other/unknown
 * Anything missing or unknown lands in the last bucket so the list still
 * renders predictably.
 */
const DEFAULT_COLOR_ORDER: Record<string, number> = {
  SPARKLING: 0,
  WHITE: 1,
  ROSE: 2,
  PINK_ROSE: 2, // alias — current data uses this
  RED: 3,
  SWEET: 4,
  FORTIFIED: 5,
  ORANGE: 6,
  OTHER: 6,
};

const defaultColorRank = (color: string | null | undefined): number => {
  if (!color) return 7;
  const rank = DEFAULT_COLOR_ORDER[color];
  return rank == null ? 6 : rank;
};

/**
 * Resolve the bottle price for sorting. Prefer `bottlePrice` (W3+ canonical
 * field) and fall back to the legacy `price` alias so both old and new
 * payloads sort correctly.
 */
const bottlePriceOf = (item: WineListRenderItem): number | null =>
  item.bottlePrice ?? item.price ?? null;

/**
 * Parse `displayVintage` into a numeric year. Non-numeric (NV / MV / blank)
 * returns null and sorts to the end of the list in either vintage direction.
 */
const vintageYearOf = (item: WineListRenderItem): number | null => {
  if (!item.vintage) return null;
  const n = parseInt(item.vintage, 10);
  return Number.isFinite(n) ? n : null;
};

/**
 * Generic comparator that always pushes nulls to the end, and falls back to
 * a numeric comparison in the requested direction for non-null pairs.
 */
const compareNullableNumber = (
  a: number | null,
  b: number | null,
  ascending: boolean,
): number => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return ascending ? a - b : b - a;
};

/**
 * Pure sort. Never mutates the input array.
 *
 * "Default order" groups by color in natural reading sequence (sparkling →
 * white → rosé → red → sweet → fortified → other), with stable ordering
 * inside each color group so the server-supplied position is preserved as
 * the secondary key.
 */
export const sortWineListItems = (
  items: WineListRenderItem[],
  mode: WineListSortMode,
): WineListRenderItem[] => {
  if (mode === 'DEFAULT') {
    // JS Array.prototype.sort is stable since ES2019, so equal-rank items
    // keep their input order — i.e. the server's section/position order.
    return [...items].sort((a, b) => defaultColorRank(a.color) - defaultColorRank(b.color));
  }

  const arr = [...items];
  switch (mode) {
    case 'PRICE_ASC':
      arr.sort((a, b) => compareNullableNumber(bottlePriceOf(a), bottlePriceOf(b), true));
      return arr;
    case 'PRICE_DESC':
      arr.sort((a, b) => compareNullableNumber(bottlePriceOf(a), bottlePriceOf(b), false));
      return arr;
    case 'VINTAGE_OLD_FIRST':
      arr.sort((a, b) => compareNullableNumber(vintageYearOf(a), vintageYearOf(b), true));
      return arr;
    case 'VINTAGE_YOUNG_FIRST':
      arr.sort((a, b) => compareNullableNumber(vintageYearOf(a), vintageYearOf(b), false));
      return arr;
    case 'COLOR_WHITE_FIRST':
      arr.sort((a, b) => compareNullableNumber(colorRank(a.color), colorRank(b.color), true));
      return arr;
    case 'COLOR_RED_FIRST':
      arr.sort((a, b) => compareNullableNumber(colorRank(a.color), colorRank(b.color), false));
      return arr;
  }
};
